// src/scripts/sqlApiHelper/safeApi.js

import { auth, db, authReady } from "@/firebase";
import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  serverTimestamp,
  orderBy,
  limit,
  documentId,
} from "firebase/firestore";

/*
Firestore structure

venues/{venueId}/safe_checks/{doc}

users/{uid}
  displayName
*/

function clean(v) {
  return String(v ?? "").trim();
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function must(name, value) {
  if (!value) throw new Error(`Missing ${name}`);
  return value;
}

async function requireUser() {
  await authReady;
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  return user;
}

function safeChecksCol(venueId) {
  return collection(db, "venues", venueId, "safe_checks");
}

function usersCol() {
  return collection(db, "users");
}

function parseISODateLocal(iso) {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toISODateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysBefore(iso, days) {
  const d = parseISODateLocal(iso);
  if (!d) return "";
  const copy = new Date(d);
  copy.setDate(copy.getDate() - days);
  return toISODateLocal(copy);
}

function isDateInRange(iso, start, end) {
  return iso >= start && iso <= end;
}

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

function isoDateOrBlank(v) {
  const s = clean(v);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
}

function timestampToMillis(value) {
  if (!value) return 0;

  if (typeof value?.toMillis === "function") {
    return value.toMillis();
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

/* -------------------------------------------------- */
/* GET USER DISPLAY NAME */
/* -------------------------------------------------- */

async function getUserDisplayName(uid) {
  if (!uid) return "—";

  try {
    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) return "—";

    return snap.data()?.displayName || "—";
  } catch {
    return "—";
  }
}

async function getUserDisplayNameMap(uids = []) {
  const unique = [...new Set(uids.map(clean).filter(Boolean))];
  const out = new Map();

  if (!unique.length) return out;

  unique.forEach((uid) => out.set(uid, "—"));

  const chunks = chunkArray(unique, 10);

  await Promise.all(
    chunks.map(async (uidChunk) => {
      try {
        const q = query(usersCol(), where(documentId(), "in", uidChunk));
        const snap = await getDocs(q);

        snap.forEach((d) => {
          out.set(d.id, clean(d.data()?.displayName) || "—");
        });
      } catch {
        await Promise.all(
          uidChunk.map(async (uid) => {
            try {
              const name = await getUserDisplayName(uid);
              out.set(uid, name || "—");
            } catch {
              out.set(uid, "—");
            }
          })
        );
      }
    })
  );

  return out;
}

/* -------------------------------------------------- */
/* HELPERS */
/* -------------------------------------------------- */

async function getLatestSafeCheckOverall(venueId) {
  const q = query(
    safeChecksCol(venueId),
    orderBy("submitted_at", "desc"),
    limit(1)
  );

  const snap = await getDocs(q);
  if (snap.empty) return null;

  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

async function getLatestSafeCheckForTradingDate(venueId, tradingDate) {
  const q = query(
    safeChecksCol(venueId),
    where("trading_date", "==", tradingDate),
    orderBy("submitted_at", "desc"),
    limit(1)
  );

  const snap = await getDocs(q);
  if (snap.empty) return null;

  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

async function getPreviousSafeCheck(venueId, currentTradingDate) {
  const currentIso = isoDateOrBlank(currentTradingDate);
  if (!venueId || !currentIso) return null;

  const snap = await getDocs(safeChecksCol(venueId));
  if (snap.empty) return null;

  let best = null;

  snap.forEach((docSnap) => {
    const row = docSnap.data() || {};
    const tradingDate = isoDateOrBlank(row.trading_date);
    const closingBalance = row.closing_balance;

    if (!tradingDate) return;
    if (tradingDate >= currentIso) return;
    if (closingBalance === null || closingBalance === undefined || closingBalance === "") return;

    const submittedMillis = timestampToMillis(row.submitted_at);
    const fallbackMillis = Date.parse(`${tradingDate}T23:59:59`) || 0;
    const effectiveSort = submittedMillis || fallbackMillis;

    if (!best || effectiveSort > best._effectiveSort) {
      best = {
        id: docSnap.id,
        ...row,
        _effectiveSort: effectiveSort,
      };
    }
  });

  return best;
}

/* -------------------------------------------------- */
/* SAFE CHECK GET */
/* -------------------------------------------------- */

export async function safeCheckGet(payload = {}) {
  const venue_id = must("venue_id", clean(payload.venue_id));
  const trading_date = must("trading_date", clean(payload.trading_date));
  const week_ending_date = must("week_ending_date", clean(payload.week_ending_date));

  await requireUser();

  const latestForDate = await getLatestSafeCheckForTradingDate(venue_id, trading_date);
  const previousSafeCheck = await getPreviousSafeCheck(venue_id, trading_date);

  const upstream_opening =
    previousSafeCheck?.closing_balance != null
      ? num(previousSafeCheck.closing_balance)
      : 0;

  return {
    ok: true,
    venue_id,
    trading_date,
    week_ending_date,
    row: latestForDate || null,
    upstream_opening,
    upstream_source: previousSafeCheck
      ? "Previous completed safe check closing balance"
      : "No prior safe check",
  };
}

/* -------------------------------------------------- */
/* UPSERT */
/* -------------------------------------------------- */

export async function safeCheckUpsert(payload = {}) {
  const user = await requireUser();

  const venue_id = must("venue_id", clean(payload.venue_id));
  const trading_date = must("trading_date", clean(payload.trading_date));
  const week_ending_date = must("week_ending_date", clean(payload.week_ending_date));

  const previousSafeCheck = await getPreviousSafeCheck(venue_id, trading_date);

  const opening_balance =
    payload.opening_balance !== null &&
    payload.opening_balance !== undefined &&
    payload.opening_balance !== ""
      ? num(payload.opening_balance)
      : previousSafeCheck?.closing_balance != null
        ? num(previousSafeCheck.closing_balance)
        : 0;

  const petty_cash = num(payload.petty_cash);
  const counted_cash = num(payload.counted_cash);
  const closing_balance = num(payload.closing_balance);
  const variance =
    payload.variance !== null &&
    payload.variance !== undefined &&
    payload.variance !== ""
      ? num(payload.variance)
      : closing_balance - opening_balance;

  const floats = num(payload.floats);

  const docData = {
    venue_id,
    trading_date,
    week_ending_date,

    opening_balance,
    petty_cash,
    counted_cash,
    closing_balance,
    variance,
    floats,

    notes_breakdown: payload.notes_breakdown || {},
    coins_breakdown: payload.coins_breakdown || {},

    submitted_by_uid: user.uid,
    submitted_by_email: user.email ?? null,

    submitted_at: serverTimestamp(),
  };

  const ref = await addDoc(safeChecksCol(venue_id), docData);

  return {
    ok: true,
    id: ref.id,
    row: {
      id: ref.id,
      ...docData,
    },
    upstream_source: previousSafeCheck
      ? "Previous completed safe check closing balance"
      : "No prior safe check",
  };
}

/* -------------------------------------------------- */
/* SAFE CHECK LIST (LAST 14 DAYS) */
/* -------------------------------------------------- */

export async function safeCheckListWeek({ venue_id, week_ending_date } = {}) {
  const v = must("venue_id", clean(venue_id));
  const endIso = must("week_ending_date", clean(week_ending_date));

  await requireUser();

  const startIso = daysBefore(endIso, 13);

  const snap = await getDocs(safeChecksCol(v));
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const filtered = all.filter((r) =>
    isDateInRange(clean(r.trading_date), startIso, endIso)
  );

  filtered.sort((a, b) => {
    const ad = clean(a.trading_date);
    const bd = clean(b.trading_date);

    if (ad !== bd) return bd.localeCompare(ad);

    const as =
      timestampToMillis(a.submitted_at) ||
      timestampToMillis(a.created_at) ||
      timestampToMillis(a.updated_at) ||
      0;

    const bs =
      timestampToMillis(b.submitted_at) ||
      timestampToMillis(b.created_at) ||
      timestampToMillis(b.updated_at) ||
      0;

    return bs - as;
  });

  const nameMap = await getUserDisplayNameMap(
    filtered.map((r) => r.submitted_by_uid)
  );

  const rows = filtered.map((r) => {
    const trading_date = clean(r.trading_date);
    const dt = parseISODateLocal(trading_date);

    return {
      id: r.id,
      trading_date,
      submitted_at: r.submitted_at ?? null,
      day: dt
        ? dt.toLocaleDateString("en-GB", { weekday: "short" })
        : "—",
      completed_by: nameMap.get(clean(r.submitted_by_uid)) || "—",
      opening_balance: num(r.opening_balance),
      closing_balance: num(r.closing_balance),
      variance: num(r.variance),
      counted_cash: num(r.counted_cash),
      petty_cash: num(r.petty_cash),
      floats: num(r.floats),
      notes_breakdown: r.notes_breakdown || {},
      coins_breakdown: r.coins_breakdown || {},
    };
  });

  return {
    ok: true,
    venue_id: v,
    week_ending_date: endIso,
    from_date: startIso,
    to_date: endIso,
    rows,
  };
}